import ToggleGroup from '../formFields/ToggleGroup';
import TimeField from '../formFields/TimeField';
import ReCAPTCHA from 'react-google-recaptcha';
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import config from '../../config';
import SelectField from '../formFields/SelectField';

// CSS
import '../../assets/scss/AppointmentForm.css';
import TextField from '../formFields/TextField';
import apiRequest from '../../handles/apiRequest';

// Variables
const {CAPTCHA_KEY} = config;
const privacyFile = 'https://www.maybank2u.com.my/iwov-resources/maybank/' +
    'document/my/en/personal/PDPA-mbb.pdf';

const AppointmentForm = (
    {
      serviceNum = '',
      formLabel = 'Make an appointment',
      preset = {},
      handleLoading = () => false,
      changePage = () => false,
      submitError = () => false,
      cancel = () => false,
    }) => {
  // React states register
  // Statuses
  const [canSubmit, setSubmit] = useState(false);
  const [showError, setShowError] = useState(false);

  // Errors
  const [errors, setErrors] = useState({});

  // Active items
  const [service, setService] = useState(preset['service'] || {});
  const [state, setState] = useState(preset['state'] || {});
  const [district, setDistrict] = useState(preset['district'] || {});
  const [branch, setBranch] = useState(preset['branch'] || {});
  const [bookingDate, setDate] = useState(preset['bookingDate'] || {});
  const [timeslot, setTime] = useState(preset['timeslot'] || '');
  const [captchaValue, setRecaptcha] = useState(preset['captchaValue'] || '');

  // Personal information
  const [customerName, setUsername] = useState(preset['customerName'] || '');
  const [idType, setIdType] = useState(preset['idType'] || {});
  const [idNumber, setUserId] = useState(preset['idNumber'] || '');
  const [contactNumber, setContact] = useState(preset['contactNumber'] || '');
  const [emailAddress, setEmail] = useState(preset['emailAddress'] || '');

  const isUpdate = preset.referenceNumber !== undefined;

  const idTypes = {
    'IC number': {pf: true},
    'Passport number': {passport: true},
  };

  // Allow submit button if all fields are entered
  const allFields = [
    service, state, district, branch, bookingDate, timeslot,
    customerName, contactNumber, captchaValue,
  ];
  useEffect(() => {
    setSubmit(allFields.reduce((acc, item) => {
      return acc && Boolean(item);
    }, true));
  }, [allFields]);

  // Methods
  const formSubmit = (e) => {
    e.preventDefault();
    // If error, show error.
    if (Object.values(errors).length) {
      setShowError(true);
    } else {
      // GA
      global.dataLayer.push({
        event: 'gaEvent',
        eventCategory: 'Online form',
        eventLabel: 'EzyQ New Appointment',
        eventAction: 'Confirmation',
      });

      const params = {
        appointmentDate: bookingDate.value,
        branchCode: branch.value,
        branchName: branch.label,
        district: district.label,
        districtId: district.value,
        service: service.label,
        serviceId: service.value,
        subService: service['subServiceName'],
        state: state.label,
        stateId: state.value,
        idType: idType.value,
        contactNumber,
        customerName,
        emailAddress: emailAddress || 'N/A',
        idNumber,
        captchaValue,
        timeslot,
      };

      // Adjust content on update case
      let endpoint = 'submit-branch-appointment-details';

      if (isUpdate) {
        // Update endpoint
        endpoint = 'update-branch-appointment-details';

        // Remove captcha
        delete params.captchaValue;

        // Add referenceNumber
        params.referenceNumber = preset.referenceNumber;
      }

      apiRequest({
        method: 'POST',
        endpoint,
        params,
      }).then((data) => {
        handleLoading(false);
        if (data.status === 'SUCCESS') {
          sessionStorage.setItem('userDetails', JSON.stringify(data));
          changePage('/page/appointment/confirm');
        } else {
          submitError(data);
        }
      }).catch(() => {
        submitError({
          status: 'ERROR',
          errorMessage: 'Could not send request to server',
        });
      });
    }
  };

  const handleError = (field, error) => {
    if (error.length) {
      errors[field] = error;
    } else {
      delete errors[field];
    }
    setErrors(errors);
  };

  // Render
  return <form onSubmit={formSubmit}>
    <div className="tab-head">{formLabel}</div>
    <div className={'note'}>Simply pick a date and time that is
      convenient to you. Once you have scheduled an appointment, you will
      receive a notification with your appointment details.
    </div>
    <div className="row row_top">
      <div className="col-md-12">
        <span className="field-label">Kindly select the appointment type
          so we can assign the right staff to best assist you.</span>
        <div className={'note'}>Note: An incorrect selection may
          delay your appointment time.
        </div>
        <ToggleGroup name="opt-group" className="radio-group"
                     onChange={setService} selected={service}
                     prepopulate={serviceNum}
        />
      </div>
    </div>
    <SelectField
        mapper={{value: 'stateId', label: 'stateName'}}
        label={'State'} onChange={setState} selected={state}
        apiEndpoint={'states'} fieldDisabled={isUpdate}
    />
    <SelectField
        mapper={{value: 'districtId', label: 'districtName'}} label={'District'}
        onChange={setDistrict} selected={district}
        requirement={{state}} apiEndpoint={`districts/${state.value}`}
        fieldDisabled={isUpdate}
    />
    <SelectField
        mapper={{value: 'branchCode', label: 'branchName'}}
        label={'Branch'} onChange={setBranch} selected={branch}
        requirement={{service, district}} apiEndpoint={'branch-details'}
        apiParams={{
          'dsitrict-id': district.value,
          'district-name': district.label,
          'service-name': service.label,
        }}
        fieldDisabled={isUpdate}
    />
    <div className="row row_top">
      <div className="col-md-12">
        <span className="field-label">Address</span>
        <div className="addrDiv">{branch.branchAddress}</div>
      </div>
    </div>
    <SelectField
        mapper={{value: 'date', label: 'date'}}
        label={'Date of appointment'} onChange={setDate} selected={bookingDate}
        requirement={{service, branch}} apiEndpoint={'available-dates'}
        disabledCheck={(option) => (!option['isAvailable'])}
        errorString={`We regret to inform you that the appointment type is currently occupied. To proceed with appointment booking, kindly choose another branch.<br> For account opening, you may drop your document at our branch to be processed and we will contact you once completed.<br> We are pleased to also inform that replacement of the Maybank Debit Cards may now be applied via this link <u><a href="https://www.maybank2u.com.my/maybank2u/malaysia/en/personal/cards/debit/debit-card-renewal.page">Maybank2u</a></u>.`}
        apiParams={{
          'branch-code': branch.value,
          'service-name': service.label,
        }} fieldDisabled={isUpdate}
    />
    <TimeField selected={preset['timeslot']}
               requirement={{service, bookingDate, branch}}
               onChange={setTime}/>
    <TextField label={'Name'} defaultValue={customerName} onChange={setUsername}
               validators={{
                 required: true,
                 name: true,
                 minLength: 5,
                 maxLength: 60,
               }} handleError={handleError} showError={showError}
               fieldDisabled={isUpdate}/>
    <SelectField options={Object.keys(idTypes)} label={'ID Type'}
                 onChange={setIdType} selected={idType}
                 fieldDisabled={isUpdate}/>
    {!idType.value || <TextField label={'ID number'} defaultValue={idNumber}
                                 onChange={setUserId} requirement={{idType}}
                                 validators={isUpdate ?
                                     {} :
                                     idTypes[idType.value]}
                                 handleError={handleError}
                                 showError={showError}
                                 fieldDisabled={isUpdate}/>}
    <TextField label={'Contact number'} defaultValue={contactNumber}
               onChange={setContact} validators={isUpdate ? {} : {phone: true}}
               handleError={handleError} showError={showError}
               fieldDisabled={isUpdate}/>
    <TextField label={'Email'} defaultValue={emailAddress}
               guide={'I have checked and confirmed that the email address provided above is correct.'}
               onChange={
      (email) => {
        setEmail(email.toLowerCase());
      }}
               validators={isUpdate ? {} : {email: true}}
               handleError={handleError}
               showError={showError} fieldDisabled={isUpdate}/>
    <div style={{
      textAlign: 'center',
      marginTop: '30px',
    }}>
      {Boolean(isUpdate) || <ReCAPTCHA
          style={{display: 'inline-block'}}
          theme="dark"
          sitekey={CAPTCHA_KEY}
          onChange={setRecaptcha}
      />}
    </div>
    <div style={{
      fontSize: '13px',
      marginTop: '10px',
    }}>
      By clicking &quot;Submit&quot;, you acknowledge that you have read,
      understood and agree to be bound by the <a
        style={{cursor: 'pointer'}}
        target="_blank" rel={'noopener noreferrer'}
        href={privacyFile}>Maybank Group&apos;s Privacy Notice</a> as may be
      amended from time to time. Where you are providing us with personal data
      of another individual, you warrant that you are authorised to consent to
      the <a style={{cursor: 'pointer'}} target="_blank"
             rel={'noopener noreferrer'} href={privacyFile}>Maybank Group&apos;s
      Privacy Notice</a> and provide us with such personal data on his/her
      behalf.
    </div>
    <div style={{paddingTop: '10px'}}>*Note: For further assistance on Maybank
      EzyQ, please contact our Maybank Group Customer Care centre at
      1300-88-6688.
    </div>
    {isUpdate ? <div>
      <div style={{textAlign: 'center'}}>
        <input type="button" onClick={cancel} value="CANCEL" className="btnDone"
        />
      </div>
      <div style={{textAlign: 'center'}}>
        <input type="submit" value="UPDATE" className="btnLogin"/>
      </div>
    </div> : <div style={{textAlign: 'center'}}>
      <input type="submit" value="SUBMIT" disabled={!canSubmit}
             className="btnLogin"/>
    </div>}
  </form>;
};

AppointmentForm.propTypes = {
  serviceNum: PropTypes.number,
  formLabel: PropTypes.string,
  preset: PropTypes.object,
  handleLoading: PropTypes.func,
  changePage: PropTypes.func,
  submitError: PropTypes.func,
  cancel: PropTypes.func,
};

export default AppointmentForm;
